import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AdminSection } from "./admin";
import { TableShell } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  getEntitlementAudit,
  repairEntitlementForOrder,
  type EntitlementAuditReport,
} from "@/lib/entitlements-audit.functions";

export const Route = createFileRoute("/_authenticated/admin/entitlements")({
  head: () => ({
    meta: [{ title: "Entitlement Audit · Admin · KAfrequency" }],
  }),
  component: EntitlementAuditPage,
});

function fmt(iso: string | null) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function EntitlementAuditPage() {
  const [report, setReport] = useState<EntitlementAuditReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await getEntitlementAudit();
      setReport(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function repair(orderId: string) {
    setRepairing(orderId);
    try {
      const res = await repairEntitlementForOrder({ data: { orderId } });
      if ("error" in res) toast.error(res.error);
      else {
        toast.success("Entitlement granted");
        await load();
      }
    } finally {
      setRepairing(null);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading audit…</div>;
  if (!report) return <div className="text-rose-400">No report</div>;

  const t = report.totals;
  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Paid orders" value={t.orders_paid} />
        <Stat label="Active entitlements" value={t.entitlements_active} tone="text-emerald-400" />
        <Stat label="Revoked" value={t.entitlements_revoked} tone="text-rose-400" />
        <Stat label="Expired" value={t.entitlements_expired} tone="text-amber-400" />
        <Stat label="Subscriptions" value={t.subscriptions_total} />
        <Stat label="Active subs" value={t.subscriptions_active} tone="text-emerald-400" />
        <Stat label="Drift rows" value={report.drift.length} tone={report.drift.length ? "text-amber-400" : "text-emerald-400"} />
        <Stat label="Sub anomalies" value={report.subscriptionAnomalies.length} tone={report.subscriptionAnomalies.length ? "text-amber-400" : "text-emerald-400"} />
      </div>

      <AdminSection
        title="Entitlement drift"
        description="Paid orders whose entitlements are missing, revoked, or expired. Repair mints the entitlement idempotently."
      >
        {report.drift.length === 0 ? (
          <div className="text-emerald-400 text-sm">No drift detected. Every paid digital order has a live entitlement.</div>
        ) : (
          <TableShell
            headers={["Order", "Product", "User", "Reason", "Created", "Action"]}
            rows={report.drift.map((row) => (
              <tr key={row.order_id}>
                <td className="px-4 py-3 font-mono text-xs">{row.order_id.slice(0, 8)}</td>
                <td className="px-4 py-3">{row.product_slug}</td>
                <td className="px-4 py-3 font-mono text-xs">{row.user_id?.slice(0, 8) ?? row.guest_email ?? "—"}</td>
                <td className="px-4 py-3 text-amber-400">{row.reason}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(row.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  {row.reason === "no_entitlement" && (
                    <Button size="sm" variant="outline" disabled={repairing === row.order_id} onClick={() => repair(row.order_id)}>
                      {repairing === row.order_id ? "Repairing…" : "Repair"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          />
        )}
      </AdminSection>

      <AdminSection
        title="Subscription anomalies"
        description="Sub records whose status disagrees with the current period. Investigate against Stripe / RevenueCat."
      >
        {report.subscriptionAnomalies.length === 0 ? (
          <div className="text-emerald-400 text-sm">No anomalies.</div>
        ) : (
          <TableShell
            headers={["User", "Tier", "Status", "Period end", "Reason"]}
            rows={report.subscriptionAnomalies.map((row, i) => (
              <tr key={`${row.user_id}-${i}`}>
                <td className="px-4 py-3 font-mono text-xs">{row.user_id.slice(0, 8)}</td>
                <td className="px-4 py-3">{row.membership_tier ?? "—"}</td>
                <td className="px-4 py-3">{row.status}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(row.current_period_end)}</td>
                <td className="px-4 py-3 text-amber-400">{row.reason}</td>
              </tr>
            ))}
          />
        )}
      </AdminSection>

      <AdminSection title="Per-product coverage" description="Active entitlements ÷ paid orders. <100% = drift.">
        <TableShell
          headers={["Product", "Paid", "Active", "Coverage"]}
          rows={report.perProduct.map((r) => (
            <tr key={r.product_slug}>
              <td className="px-4 py-3">{r.product_slug}</td>
              <td className="px-4 py-3 text-right">{r.paid_orders}</td>
              <td className="px-4 py-3 text-right">{r.active_entitlements}</td>
              <td className={`px-4 py-3 text-right ${r.coverage_pct < 100 ? "text-amber-400" : "text-emerald-400"}`}>{r.coverage_pct}%</td>
            </tr>
          ))}
        />
      </AdminSection>

      <div className="text-xs text-muted-foreground">Generated {fmt(report.generatedAt)}</div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-border/40 bg-card/40 p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone ?? ""}`}>{value.toLocaleString()}</div>
    </div>
  );
}
